import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Toaster } from '../components/ui/Toaster';
import { useSendCodeMutation, useVerifyCodeMutation, useTelegramLinkLoginMutation } from '../store/api/authApi';
import { storeAuthData } from '../utils/tokenManager';

interface ErrorResponse {
  data?: {
    error?: string;
  };
  message?: string;
}

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sendCodeMutation, { isLoading: isSendingCode }] = useSendCodeMutation();
  const [verifyCodeMutation, { isLoading: isVerifyingCode }] = useVerifyCodeMutation();
  const [telegramLinkLoginMutation, { isLoading: isLinkLogin }] = useTelegramLinkLoginMutation();

  const [step, setStep] = useState<'telegramId' | 'code'>('telegramId');
  const [telegramId, setTelegramId] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const linkLoginAttempted = useRef(false);

  const isLoading = isSendingCode || isVerifyingCode || isLinkLogin;

  useEffect(() => {
    // If already authenticated, redirect to dashboard
    const token = localStorage.getItem('authToken');
    if (token) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (linkLoginAttempted.current) return;
    const params = new URLSearchParams(window.location.search);
    const tgToken = params.get('tg_token');
    if (!tgToken) return;
    linkLoginAttempted.current = true;

    const loginWithLink = async () => {
      setError(null);
      setSuccessMessage(null);
      try {
        const result = await telegramLinkLoginMutation({ tg_token: tgToken }).unwrap();
        if (result && result.success && result.data) {
          const token = result.data.token;
          const userData = result.data.user as unknown as Record<string, unknown>;
          localStorage.setItem('authToken', token);
          storeAuthData(token, userData);
          toast.success(t('auth.login.success') || 'تم تسجيل الدخول بنجاح!');
          navigate('/admin/dashboard', { replace: true });
        } else {
          const errorMsg = result && 'error' in result ? String(result.error) : 'فشل تسجيل الدخول عبر الرابط.';
          setError(errorMsg);
          toast.error(errorMsg);
        }
      } catch (err) {
        const errorResponse = err as ErrorResponse;
        const errorMessage =
          errorResponse?.data?.error || errorResponse?.message || 'فشل تسجيل الدخول عبر الرابط.';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    };

    void loginWithLink();
  }, [navigate]);

  const handleSendCode = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    const id = parseInt(telegramId.trim(), 10);
    if (!id || isNaN(id)) {
      setError('يرجى إدخال Telegram ID صحيح');
      toast.error('يرجى إدخال Telegram ID صحيح');
      return;
    }

    try {
      const result = await sendCodeMutation({ telegramId: id }).unwrap();

      if (result && 'success' in result && result.success) {
        const message = 'message' in result ? String(result.message) : 'تم إرسال كود التحقق إلى حسابك على Telegram';
        setSuccessMessage(message);
        toast.success(message);
        setStep('code');
      } else {
        const errorMsg = result && 'error' in result ? String(result.error) : 'فشل إرسال كود التحقق. يرجى المحاولة مرة أخرى.';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      const errorResponse = err as ErrorResponse;
      const errorMessage =
        errorResponse?.data?.error ||
        errorResponse?.message ||
        'فشل إرسال كود التحقق. يرجى المحاولة مرة أخرى.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    const id = parseInt(telegramId.trim(), 10);
    if (!code.trim() || code.trim().length !== 6) {
      setError('يرجى إدخال كود التحقق المكون من 6 أرقام');
      toast.error('يرجى إدخال كود التحقق المكون من 6 أرقام');
      return;
    }

    try {
      const result = await verifyCodeMutation({ telegramId: id, code: code.trim() }).unwrap();

      if (result && 'success' in result && result.success && result.data) {
        // Store token and user data
        const token = result.data.token;
        const userData = result.data.user as unknown as Record<string, unknown>;
        
        // Store token in localStorage
        localStorage.setItem('authToken', token);
        
        // Store user data
        storeAuthData(token, userData);

        toast.success(t('auth.login.success') || 'تم تسجيل الدخول بنجاح!');

        // Redirect to dashboard
        navigate('/admin/dashboard', { replace: true });
      } else {
        const errorMsg = result && 'error' in result ? String(result.error) : 'فشل تسجيل الدخول. يرجى المحاولة مرة أخرى.';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      const errorResponse = err as ErrorResponse;
      const errorMessage =
        errorResponse?.data?.error ||
        errorResponse?.message ||
        'فشل تسجيل الدخول. يرجى المحاولة مرة أخرى.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleBack = () => {
    setStep('telegramId');
    setCode('');
    setError(null);
    setSuccessMessage(null);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Toaster />
      <div className="w-full max-w-md">
        <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-8 shadow-xl border border-neutral-dark/50">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-heading font-bold text-brand-accent mb-2">
              {t('auth.login.title') || 'تسجيل الدخول'}
            </h1>
            <p className="text-neutral-medium">
              {step === 'telegramId'
                ? t('auth.login.subtitle') || 'سجل الدخول للوصول إلى لوحة التحكم'
                : t('auth.login.codeStep') || 'أدخل كود التحقق المرسل إلى حسابك على Telegram'}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-danger/20 border border-danger/50 rounded-lg">
              <p className="text-danger text-sm">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="mb-6 p-4 bg-success/20 border border-success/50 rounded-lg">
              <p className="text-success text-sm">{successMessage}</p>
            </div>
          )}

          {step === 'telegramId' ? (
              <form onSubmit={(e) => { void handleSendCode(e); }}>
              <div className="mb-6">
                <label htmlFor="telegramId" className="block text-sm font-medium text-neutral-light mb-2">
                  {t('auth.login.telegramId') || 'Telegram ID'}
                </label>
                <input
                  id="telegramId"
                  type="text"
                  inputMode="numeric"
                  value={telegramId}
                  onChange={(e) => setTelegramId(e.target.value)}
                  placeholder={t('auth.login.telegramIdPlaceholder') || 'أدخل Telegram ID الخاص بك'}
                  disabled={isLoading}
                  className="w-full px-4 py-3 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSendingCode ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-brand-primary"></div>
                    <span>{t('common.loading') || 'جاري الإرسال...'}</span>
                  </>
                ) : (
                  <span>{t('auth.login.sendCode') || 'إرسال كود التحقق'}</span>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={(e) => { void handleVerifyCode(e); }}>
              <div className="mb-6">
                <label htmlFor="code" className="block text-sm font-medium text-neutral-light mb-2">
                  {t('auth.login.code') || 'كود التحقق'}
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(value);
                  }}
                  placeholder={t('auth.login.codePlaceholder') || 'أدخل الكود المكون من 6 أرقام'}
                  disabled={isLoading}
                  className="w-full px-4 py-3 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed text-center text-2xl tracking-widest"
                  required
                  autoFocus
                />
                <p className="mt-2 text-xs text-neutral-medium text-center">
                  {t('auth.login.codeHint') || 'أدخل الكود المكون من 6 أرقام المرسل إلى حسابك على Telegram'}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={isLoading}
                  className="flex-1 py-3 px-4 bg-neutral-medium text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-medium/80 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.back') || 'رجوع'}
                </button>
                <button
                  type="submit"
                  disabled={isLoading || code.length !== 6}
                  className="flex-1 py-3 px-4 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isVerifyingCode ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-brand-primary"></div>
                      <span>{t('common.loading') || 'جاري التحقق...'}</span>
                    </>
                  ) : (
                    <span>{t('auth.login.verifyCode') || 'تسجيل الدخول'}</span>
                  )}
                </button>
              </div>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-neutral-medium">
            {step === 'telegramId'
              ? t('auth.login.description') ||
                'أدخل Telegram ID الخاص بك لإرسال كود التحقق إلى حسابك على Telegram'
              : t('auth.login.codeDescription') ||
                'تحقق من حسابك على Telegram للحصول على كود التحقق المكون من 6 أرقام'}
          </p>
        </div>
      </div>
    </div>
  );
}
